import { SportList } from 'components/SportList/SportList';
import styles from '../SportsBetting/SportsBetting.module.css';
import { useTranslation } from 'react-i18next';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { ResultsMatchTable } from 'components/ResultsMatchTable/ResultsMatchTable';
import useAsyncEffect from 'use-async-effect';
import {
  getResultsDayEvents,
  getResultsEventsByLeague,
  getSports,
  searchEventsByTeamName,
} from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import {
  ModernSidebarPortal,
  useModernDesktop,
} from 'components/Layout/ModernDesktopShell';
import { LeagueContext, type SelectedLeagueEntry } from 'context/leagueContext';
import { MatchOffer } from 'types/MatchOffer';
import type { ResultsLeagueBlock } from 'types/ResultsLeagueBlock';
import { SportIDs } from 'types/enums';
import Modal from 'react-modal';
import { useLocation } from 'react-router-dom';
import { SlipContext } from 'context/slipContext';
import { SportsBettingRightSide } from 'components/SportsBettingRightSide/SportsBettingRightSide';
import { Tooltip } from 'react-tooltip';
import { Tooltip as SearchInputTooltip } from 'components/Tooltip/Tooltip';
import { SportsBettingSearchResults } from 'components/SportsBettingSearchResults/SportsBettingSearchResults';
import { TimeFilter } from 'components/TimeFilter/TimeFilter';
import { eventsToFeedBlocks, yyyymmddLocal } from 'utils/resultsFeed.utils';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_QUERY_LENGTH = 2;

type LeagueMatchesRow = {
  matches: MatchOffer[];
  leagueId: number;
  sportId: number;
  groupLabel?: string | null;
};

/**
 * Same shell as Sports Betting: search, LONG-TERM time filter, sport → country → leagues.
 * Landing: Today/Yesterday tabs + day feed. With leagues: multi-day league table, no tabs.
 */
export const ResultsBetting = () => {
  const { t } = useTranslation();
  const modernDesktop = useModernDesktop();
  const location = useLocation();
  const includeFisFromUrl = useMemo(() => {
    const p = new URLSearchParams(location.search || '');
    const raw = p.get('fi') || p.get('include_fi');
    if (!raw) return undefined;
    const ids = raw
      .split(',')
      .map((s) => parseInt(String(s).trim(), 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
    return ids.length ? ids : undefined;
  }, [location.search]);
  const [allSports, setAllSports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { leagues } = useContext(LeagueContext) as {
    leagues: SelectedLeagueEntry[];
  };
  const [leaguesMatches, setLeaguesMatches] = useState<LeagueMatchesRow[]>([]);
  const [isFetchingData, setFetchingData] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<{
    query: string;
    results: Map<number, MatchOffer[]>;
  } | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [emptySearchQuery, setEmptySearchQuery] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);
  const { entries } = useContext(SlipContext);

  const [feedBlocksByTab, setFeedBlocksByTab] = useState<{
    today: ResultsLeagueBlock[];
    yesterday: ResultsLeagueBlock[];
  } | null>(null);
  const [feedLoading, setFeedLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const invalidatePendingSearch = useCallback(() => {
    searchSeqRef.current += 1;
  }, []);

  const executeSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults(null);
      setEmptySearchQuery(null);
      setTooltipOpen(false);
      return;
    }
    const mySeq = ++searchSeqRef.current;
    try {
      const results = await searchEventsByTeamName(q);
      if (mySeq !== searchSeqRef.current) return;
      setTooltipOpen(false);
      if (results && results.size > 0) {
        setSearchResults({
          query: q,
          results: results as Map<number, MatchOffer[]>,
        });
        setEmptySearchQuery(null);
      } else {
        setSearchResults(null);
        setEmptySearchQuery(q);
      }
    } catch {
      if (mySeq !== searchSeqRef.current) return;
      setSearchResults(null);
      setEmptySearchQuery(q);
    }
  }, []);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const value = event.target.value;
    const q = value.trim();

    if (q.length === 0) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      setTooltipOpen(false);
      return;
    }

    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const latest = searchInputRef.current?.value ?? value;
      void executeSearch(latest);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const value = (event.target as HTMLInputElement).value;
    const q = value.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      setTooltipOpen(true);
      return;
    }
    void executeSearch(value);
  };

  useAsyncEffect(async () => {
    try {
      const gamesResponse = await getSports(undefined, { results: true });
      setIsLoading(false);
      setAllSports(gamesResponse ?? []);
    } catch {
      setIsLoading(false);
    }
  }, []);

  useLayoutEffect(() => {
    const wanted = new Set(leagues.map((l) => l.leagueId));
    setLeaguesMatches((prev) => prev.filter((x) => wanted.has(x.leagueId)));
  }, [leagues]);

  const leaguesRef = useRef(leagues);
  const leaguesMatchesRef = useRef(leaguesMatches);
  leaguesRef.current = leagues;
  leaguesMatchesRef.current = leaguesMatches;

  const fetchQueueKey = useMemo(
    () =>
      `${leagues.map((l) => l.leagueId).join(',')}|${leaguesMatches.map((m) => m.leagueId).join(',')}`,
    [leagues, leaguesMatches]
  );

  useAsyncEffect(async () => {
    const have = new Set(leaguesMatchesRef.current.map((m) => m.leagueId));
    const pending = leaguesRef.current.filter((l) => !have.has(l.leagueId));
    if (pending.length === 0) {
      setFetchingData(false);
      return;
    }
    if (searchResults) {
      setSearchResults(null);
      setEmptySearchQuery(null);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    }
    setFetchingData(true);
    const additions: LeagueMatchesRow[] = [];
    for (const entry of pending) {
      const newLeagueMatches = await getResultsEventsByLeague(
        entry.leagueId,
        entry.sportId as SportIDs,
        true,
        includeFisFromUrl
      );
      additions.push({
        matches: newLeagueMatches ?? [],
        leagueId: entry.leagueId,
        sportId: entry.sportId,
        groupLabel: entry.groupLabel ?? null,
      });
    }
    setLeaguesMatches((prev) => {
      const have2 = new Set(prev.map((p) => p.leagueId));
      const merged = [...prev];
      for (const row of additions) {
        if (!have2.has(row.leagueId)) {
          merged.push(row);
          have2.add(row.leagueId);
        }
      }
      return merged;
    });
    setFetchingData(false);
  }, [fetchQueueKey, searchResults, includeFisFromUrl]);

  useAsyncEffect(async () => {
    if (searchResults || leagues.length > 0) {
      setFeedBlocksByTab(null);
      setFeedLoading(false);
      return;
    }
    setFeedLoading(true);
    try {
      const now = new Date();
      const todayKey = yyyymmddLocal(now);
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yesterdayKey = yyyymmddLocal(y);
      const [tEv, yEv] = await Promise.all([
        getResultsDayEvents(todayKey),
        getResultsDayEvents(yesterdayKey),
      ]);
      setFeedBlocksByTab({
        today: eventsToFeedBlocks(tEv ?? []),
        yesterday: eventsToFeedBlocks(yEv ?? []),
      });
    } catch {
      setFeedBlocksByTab({ today: [], yesterday: [] });
    }
    setFeedLoading(false);
  }, [leagues.length, searchResults]);

  const searchAndFilter = (
    <div className={styles.searchAndFilter}>
      <div data-tooltip-id="results-search-error" data-tooltip-place="bottom">
        <input
          type="text"
          placeholder={t('search_label')}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          ref={searchInputRef}
          autoComplete="off"
        />
        {tooltipOpen && (
          <Tooltip
            clickable
            className="blank-tooltip"
            id="results-search-error"
            arrowColor="#079c20"
            isOpen={tooltipOpen}
          >
            <SearchInputTooltip
              title="Search"
              text={t('betting_search_error')}
              onClose={() => setTooltipOpen(false)}
            />
          </Tooltip>
        )}
      </div>

      <div className={styles.timeFilterWrapper}>
        <TimeFilter />
      </div>
    </div>
  );

  return (
    <div className={styles.container} data-modern-desktop={modernDesktop}>
      {modernDesktop ? (
        <ModernSidebarPortal>
          {isLoading ? (
            <Loader />
          ) : (
            <SportList sports={allSports} resultsMode />
          )}
        </ModernSidebarPortal>
      ) : (
        <div className={styles.sportsWrapper}>
          {searchAndFilter}
          {isLoading ? (
            <Loader />
          ) : (
            <SportList sports={allSports} resultsMode />
          )}
        </div>
      )}
      <div className={styles.mainContent}>
        <div className={styles.sprotsBettingLabel}>{t('results_label')}</div>
        {modernDesktop && searchAndFilter}
        {searchResults ? (
          <SportsBettingSearchResults
            results={searchResults}
            useResultsTable
            closeSearchResults={() => {
              setSearchResults(null);
              setEmptySearchQuery(null);
            }}
          />
        ) : emptySearchQuery !== null ? (
          <div className={styles.emptySearchMain}>
            <ThemeIcon icon="info-full" alt="" variant="inline" />
            <span>
              {t('no_search_results').replace(
                '{search_query}',
                emptySearchQuery
              )}
            </span>
          </div>
        ) : (
          <>
            {leaguesMatches.length > 0 ? (
              <ResultsMatchTable blocks={leaguesMatches} />
            ) : feedLoading ? (
              <Loader />
            ) : feedBlocksByTab ? (
              <ResultsMatchTable
                blocks={[]}
                showDayTabs
                feedBlocksByTab={feedBlocksByTab}
              />
            ) : (
              <div className={styles.emptyHint}>
                {t('results_pick_leagues_hint')}
              </div>
            )}
          </>
        )}
      </div>
      <div className={styles.rightSide} data-slip-column>
        <SportsBettingRightSide slipEntries={entries} />
      </div>
      <Modal
        isOpen={isFetchingData}
        className={styles.loadingModal}
        ariaHideApp={false}
      >
        <div className={styles.loaderWrapper}>
          {isFetchingData && <Loader />}
        </div>
      </Modal>
    </div>
  );
};
